import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosClient from '../axios-client';
import { useUser } from '../contexts/UserContext';

// Interface for job data
export interface JobData {
  _id: string;
  title: string;
  company: string;
  location: string;
  job_url: string;
  [key: string]: any;
}

interface JobModalContextType {
  openJobModal: (jobId: string, jobData?: JobData) => void;
  closeJobModal: () => void;
  currentJob: JobData | null;
  isModalOpen: boolean;
  isLoading: boolean;
  error: string | null;
  closeJobModalAfterApplication: () => void;
}

const JobModalContext = createContext<JobModalContextType | undefined>(undefined);

// Helper function to detect if a job ID is in the URL
function detectJobIdInURL() {
  const currentPath = window.location.pathname;
  const match = currentPath.match(/\/job\/([^/]+)/);
  return match ? match[1] : null;
}

// Helper to extract the job ID from any path string.
const extractJobIdFromPath = (path: string): string | null => {
  const match = path.match(/\/job\/([^/]+)/);
  return match ? match[1] : null;
};

export const JobModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // NOTE: do NOT cache the initial job ID at the module level; compute it
  // fresh for every mount so that stale values don't leak across route
  // transitions.

  // Determine at mount whether the URL already contains a job ID.
  const initialJobIdInPath = extractJobIdFromPath(window.location.pathname);

  const [currentJob, setCurrentJob] = useState<JobData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(!!initialJobIdInPath);
  const [isLoading, setIsLoading] = useState<boolean>(!!initialJobIdInPath);
  const [error, setError] = useState<string | null>(null);
  const [isInRouterContext, setIsInRouterContext] = useState<boolean>(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState<boolean>(false);
  // Guard against the "close → reopen" flicker when the modal URL is replaced
  const justClosedRef = useRef<boolean>(false);
  
  // Always call hooks at the top level, even if we don't use their values
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useUser();

  // Check if this is a direct page load with a job ID - run this ASAP
  useEffect(() => {
    // This effect needs to run on first render for priority loading
    const checkForDirectJobNavigation = async () => {
      const jobId = extractJobIdFromPath(location.pathname);
      
      if (jobId) {
        // Skip initial load for demo jobs - let applicationTracker handle them
        if (jobId.startsWith('demo-')) {
          setInitialLoadComplete(true);
          setIsLoading(false);
          return;
        }
        
        try {
          // Fetch job data
          const response = await axiosClient.get(`/jobs/${jobId}`);
          
          if (response.data) {
            setCurrentJob(response.data);
          } else {
            setError('Job not found');
            cleanUrlPath();
          }
        } catch (error) {
          console.error(`Error fetching job: ${jobId}`, error);
          setError('Failed to load job details');
          cleanUrlPath();
        } finally {
          setIsLoading(false);
          setInitialLoadComplete(true);
        }
      } else {
        // No job ID in URL, just mark initial load as complete
        setInitialLoadComplete(true);
      }
    };
    
    checkForDirectJobNavigation();
  }, []); // Empty dependency array ensures this runs once on mount

  // Initialize and check if we're in a router context
  useEffect(() => {
    setIsInRouterContext(true);
  }, []);

  // Monitor URL for job ID changes after initial load
  useEffect(() => {
    // Only proceed if initial load is complete and we're in router context
    if (!initialLoadComplete || !isInRouterContext) return;
    
    const pathname = location.pathname;
    const jobId = extractJobIdFromPath(pathname);
    
    if (jobId && !isModalOpen) {
      // If we literally just closed the modal, ignore this jobId to avoid a flicker
      if (justClosedRef.current) {
        return;
      }

      // Skip opening modal for demo jobs - let applicationTracker handle them
      if (jobId.startsWith('demo-')) {
        return;
      }

      fetchAndOpenJob(jobId);
    } else if (!jobId && isModalOpen) {
      // URL changed and no job ID, but don't automatically close the modal
      // We'll only clean up the URL but keep the modal open
    } else if (!jobId) {
      // Once the URL no longer contains a jobId, reset the guard so future clicks work
      if (justClosedRef.current) {
        justClosedRef.current = false;
      }
    }
  }, [location, isModalOpen, isInRouterContext, initialLoadComplete]);

  // Fetch job data by ID
  const fetchAndOpenJob = async (jobId: string) => {
    if (!jobId) return;
    
    // Skip fetching for demo jobs - they should be handled by the component that opened them
    if (jobId.startsWith('demo-')) {
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await axiosClient.get(`/jobs/${jobId}`);
      
      if (response.data) {
        setCurrentJob(response.data);
        setIsModalOpen(true);
      } else {
        setError('Job not found');
        cleanUrlPath();
      }
    } catch (error) {
      console.error(`Error fetching job: ${jobId}`, error);
      setError('Failed to load job details');
      cleanUrlPath();
    } finally {
      setIsLoading(false);
    }
  };

  // Get base path without job ID
  const getBasePath = (path: string): string => {
    // Remove "/job/:id" and any query string/hash that may follow.
    const withoutJob = path.replace(/\/job\/[^/]+$/, '');
    return withoutJob.split('?')[0].split('#')[0];
  };

  // Remove job ID from URL
  const cleanUrlPath = () => {
    const currentPath = window.location.pathname;
    if (currentPath.includes('/job/')) {
      const basePath = getBasePath(currentPath);
      navigate(basePath, { replace: true });
      console.log('Cleaned URL path:', basePath);
    }
  };

  // Open job modal with either data or by fetching
  const openJobModal = (jobId: string, jobData?: JobData) => {
    if (jobData) {
      // Ensure job_url is a string
      const safeJobData = {
        ...jobData,
        job_url: jobData.job_url || ""
      };
      setCurrentJob(safeJobData);
      setIsModalOpen(true);
    } else {
      fetchAndOpenJob(jobId);
    }
    
    // Update URL without double slashes
    const currentPath = window.location.pathname;
    // Remove any existing job ID part first
    let basePath = getBasePath(currentPath);
    if (basePath.endsWith('/')) basePath = basePath.slice(0, -1);
    // Construct new path with job ID
    const newPath = `${basePath}/job/${jobId}`; // guaranteed single slash
    
    // Use navigate instead of window.history for better React Router integration
    navigate(newPath, { state: { bgLocation: location } });
  };

  // Close modal and clean URL
  const closeJobModal = () => {
    // First explicitly set modal state to closed to ensure UI updates
    setIsModalOpen(false);
    setCurrentJob(null);
    // Mark that we've just closed the modal so that the watcher doesn't immediately reopen it
    justClosedRef.current = true;
    
    // Dispatch custom event to ensure modal closes
    window.dispatchEvent(new CustomEvent('job-modal-close'));
    
    // If the visitor is not logged in, send them to the login page and
    // remember where they came from so that, after auth, we can reopen the
    // modal automatically.
    if (!isAuthenticated) {
      const currentPath = window.location.pathname;
      const basePath = getBasePath(currentPath);
      try {
        localStorage.setItem('redirectAfterLogin', basePath);
      } catch {}
      navigate('/auth/login', { replace: true });
      return;
    }

    // Logged-in users: always strip the job segment and stay on the list/dashboard.
    const currentPath = window.location.pathname;
    if (currentPath.includes('/job/')) {
      const basePath = getBasePath(currentPath);
      navigate(basePath, { replace: true });
    }
  };

  // Special method for closing after application confirmation
  const closeJobModalAfterApplication = () => {
    // First explicitly set modal state to closed to ensure UI updates
    setIsModalOpen(false);
    setCurrentJob(null);
    
    // Dispatch custom event to ensure overlay is removed
    window.dispatchEvent(new CustomEvent('job-modal-close'));
    
    // Then clean up the URL
    const currentPath = window.location.pathname;
    
    // Check if we have a job ID in the URL
    if (currentPath.includes('/job/')) {
      // Extract base path by removing the job ID part
      const basePath = getBasePath(currentPath);
      
      // Use navigate for React Router integration
      navigate(basePath, { replace: true });
    }
  };

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const jobId = extractJobIdFromPath(window.location.pathname);
      
      if (!jobId && isModalOpen) {
        // We navigated away from a job page, close the modal
        setIsModalOpen(false);
        setCurrentJob(null);
      } else if (jobId && !isModalOpen) {
        // Skip navigation for demo jobs - let applicationTracker handle them
        if (jobId.startsWith('demo-')) {
          return;
        }
        
        // We navigated to a job page, open the modal
        fetchAndOpenJob(jobId);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isModalOpen]);

  return (
    <JobModalContext.Provider value={{
      openJobModal,
      closeJobModal,
      currentJob,
      isModalOpen,
      isLoading,
      error,
      closeJobModalAfterApplication
    }}>
      {children}
    </JobModalContext.Provider>
  );
};

// Custom hook to use the job modal context
export const useJobModal = () => {
  const context = useContext(JobModalContext);
  if (context === undefined) {
    throw new Error('useJobModal must be used within a JobModalProvider');
  }
  return context;
}; 